import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { MembershipStatus } from '../components/MembershipStatus';
import { YouTubeStatus } from '../components/YouTubeStatus';
import HostControlsModal from '../components/HostControlsModal';
import BrandingManager from '../components/BrandingManager';
import VenueAdsManager from '../components/VenueAdsManager';
import { useSubscription } from '../hooks/useSubscription';
import { collection, getDocs, query, where, limit as fsLimit } from 'firebase/firestore';
import { db } from '../firebase';
import { createReducedParty } from '../services/reducedPartyActions';
import {
  reducedModeEnabled,
  reducedModeHighlights,
} from '../utils/reducedMode';

const DEFAULT_PARTY_SETTINGS = {
  maxQueueSize: 50,
  allowDuplicates: false,
  requireAuth: false,
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, userData, signOut, isAdmin } = useAuth();
  const { toast } = useToast();
  const [isCreatingParty, setIsCreatingParty] = useState(false);
  const [partyName, setPartyName] = useState('');
  const [activePartyId, setActivePartyId] = useState<string | null>(null);
  const [showHostControls, setShowHostControls] = useState(false);
  const [showBrandingManager, setShowBrandingManager] = useState(false);
  const [showVenueAdsManager, setShowVenueAdsManager] = useState(false);
  const [isLaunchingFrogLab, setIsLaunchingFrogLab] = useState(false);
  const [isLaunchingImposter, setIsLaunchingImposter] = useState(false);
  const subscription = useSubscription();

  // Find any active party for this user (owner)
  useEffect(() => {
    const fetchActiveParty = async () => {
      if (!user) {
        setActivePartyId(null);
        return;
      }
      try {
        const q = query(
          collection(db, 'parties'),
          where('createdBy', '==', user.uid),
          fsLimit(5)
        );
        const snap = await getDocs(q);
        const mine = snap.docs
          .map(d => ({ id: d.id, ...(d.data() as any) }))
          .find(p => p.isActive === true);
        setActivePartyId(mine ? mine.id : null);
      } catch (e) {
        setActivePartyId(null);
      }
    };
    fetchActiveParty();
  }, [user?.uid]);

  const callCreatePartyCallable = async (name: string, settingsOverride?: Record<string, unknown>) => {
    const createPartyFunction = httpsCallable(functions, 'party');
    const result = await createPartyFunction({
      action: 'create',
      name,
      settings: {
        ...DEFAULT_PARTY_SETTINGS,
        ...(settingsOverride || {}),
      }
    });
    return result.data as any;
  };

  const callCreatePartyHttp = async (name: string, settingsOverride?: Record<string, unknown>) => {
    const token = await user?.getIdToken();
    const res = await fetch('https://us-central1-qroke-a6fed.cloudfunctions.net/parties', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({
        action: 'create',
        name,
        settings: {
          ...DEFAULT_PARTY_SETTINGS,
          ...(settingsOverride || {}),
        }
      })
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`HTTP ${res.status} ${res.statusText}: ${text}`);
    }
    return res.json();
  };

  const handleCreateParty = async () => {
    if (!partyName.trim()) {
      toast('Please enter a party name', 'warning');
      return;
    }

    setIsCreatingParty(true);
    try {
      const name = partyName.trim();
      let data: any;
      if (reducedModeEnabled) {
        data = await createReducedParty(name, DEFAULT_PARTY_SETTINGS);
      } else {
        try {
          data = await callCreatePartyCallable(name);
        } catch (err: any) {
          data = await callCreatePartyHttp(name);
        }
      }
      
      if (data.partyId) {
        // Navigate to the party display page
        navigate(`/party/${data.partyId}`);
      } else {
        throw new Error('No party ID returned');
      }
    } catch (error: any) {
      toast(`Failed to create party: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setIsCreatingParty(false);
    }
  };

  const handleQuickStart = async () => {
    const quickPartyName = `${user?.displayName || 'Anonymous'}'s Karaoke Party`;
    setPartyName(quickPartyName);
    
    setIsCreatingParty(true);
    try {
      let data: any;
      if (reducedModeEnabled) {
        data = await createReducedParty(quickPartyName, DEFAULT_PARTY_SETTINGS);
      } else {
        try {
          data = await callCreatePartyCallable(quickPartyName);
        } catch (err: any) {
          data = await callCreatePartyHttp(quickPartyName);
        }
      }
      
      if (data.partyId) {
        navigate(`/party/${data.partyId}`);
      } else {
        throw new Error('No party ID returned');
      }
    } catch (error: any) {
      toast(`Failed to create party: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setIsCreatingParty(false);
    }
  };

  const handleLaunchFrogLab = async () => {
    const frogLabName = `${user?.displayName || 'Host'}'s Frog Lab`;
    setIsLaunchingFrogLab(true);
    try {
      let data: any;
      if (reducedModeEnabled) {
        data = await createReducedParty(frogLabName, { ...DEFAULT_PARTY_SETTINGS, gameMode: 'farkle', isGameParty: true });
      } else {
        try {
          data = await callCreatePartyCallable(frogLabName, { gameMode: 'farkle', isGameParty: true });
        } catch (err: any) {
          data = await callCreatePartyHttp(frogLabName, { gameMode: 'farkle', isGameParty: true });
        }
      }

      if (data.partyId) {
        setActivePartyId(data.partyId);
        navigate(`/party/${data.partyId}/game/farkle`);
      } else {
        throw new Error('No party ID returned');
      }
    } catch (error: any) {
      toast(`Failed to launch Frog Lab: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setIsLaunchingFrogLab(false);
    }
  };

  const handleLaunchImposter = async () => {
    const imposterPartyName = `${user?.displayName || 'Host'}'s Imposter Game`;
    setIsLaunchingImposter(true);
    try {
      let data: any;
      if (reducedModeEnabled) {
        data = await createReducedParty(imposterPartyName, { ...DEFAULT_PARTY_SETTINGS, gameMode: 'imposter', isGameParty: true });
      } else {
        try {
          data = await callCreatePartyCallable(imposterPartyName, { gameMode: 'imposter', isGameParty: true });
        } catch (err: any) {
          data = await callCreatePartyHttp(imposterPartyName, { gameMode: 'imposter', isGameParty: true });
        }
      }

      if (data.partyId) {
        setActivePartyId(data.partyId);
        navigate(`/party/${data.partyId}/game/imposter`);
      } else {
        throw new Error('No party ID returned');
      }
    } catch (error: any) {
      toast(`Failed to launch Imposter Game: ${error.message || 'Unknown error'}`, 'error');
    } finally {
      setIsLaunchingImposter(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      // Sign out error
    }
  };

  return (
    <div className="min-h-screen p-6 bg-qr-light text-qr-dark">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-display font-bold mb-2">Welcome back to QrOke!</h1>
            <p className="text-gray-700">Hi {user?.displayName || 'Karaoke Host'}, ready to start the party?</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/')}
              className="bg-white hover:bg-qr-surface border border-gray-200 px-4 py-2 rounded-lg text-qr-dark transition-colors"
              aria-label="Go Home"
            >
              Home
            </button>
            <button
              onClick={() => navigate('/instructions')}
              className="bg-white hover:bg-qr-surface border border-gray-200 px-4 py-2 rounded-lg text-qr-dark transition-colors"
              aria-label="How-To Guide"
            >
              📖 How-To
            </button>
            {subscription.tier !== 'free' && subscription.isActive && (
              <button
                onClick={() => setShowBrandingManager(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                aria-label="Custom Branding"
              >
                🎨 Branding
              </button>
            )}
            {subscription.tier !== 'free' && subscription.isActive && (
              <button
                onClick={() => setShowVenueAdsManager(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                aria-label="Venue Ads"
              >
                📺 Venue Ads
              </button>
            )}
            {activePartyId && (
              <>
                <button
                  onClick={() => setShowHostControls(true)}
                  className="bg-qr-primary hover:bg-qr-secondary text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  aria-label="Host Controls"
                >
                  🎛️ Host Controls
                </button>
                <button
                  onClick={() => navigate(`/party/${activePartyId}`)}
                  className="bg-qr-accent hover:bg-purple-500 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  aria-label="Go to Current Party"
                >
                  Current Party
                </button>
                {/* Phone-host and experimental phone-mic entry points removed from main UI */}
              </>
            )}
            {isAdmin && (
              <button
                onClick={() => navigate('/admin')}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Admin Panel
              </button>
            )}
            <button
              onClick={handleSignOut}
              className="bg-white hover:bg-qr-surface border border-gray-200 px-4 py-2 rounded-lg text-qr-dark transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Quick Start Section */}
        <div className="bg-qr-surface border border-qr-primary/30 rounded-2xl p-8 mb-8">
          {reducedModeEnabled && (
            <div className="mb-6 rounded-2xl border border-stone-200 bg-stone-50 px-5 py-4 text-stone-900">
              <p className="mt-3 text-sm leading-6 text-stone-700">
                This dashboard keeps the fast path visible: launch a party, share the QR, run the queue, and open host controls without extra setup getting in the way.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {reducedModeHighlights.map((highlight) => (
                  <span key={highlight} className="rounded-full bg-white px-3 py-1 text-xs font-medium text-stone-700 ring-1 ring-stone-200">
                    {highlight}
                  </span>
                ))}
              </div>
            </div>
          )}
          <h2 className="text-2xl font-display font-bold mb-4">🚀 Quick Start</h2>
          <p className="text-gray-700 mb-6">
            Start a karaoke party instantly with default settings. Perfect for getting the fun started right away!
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={handleQuickStart}
              disabled={isCreatingParty}
              className="bg-qr-accent hover:bg-purple-500 disabled:bg-gray-400 px-8 py-4 rounded-xl text-white font-semibold text-lg transition-all transform hover:scale-105 qr-shadow"
            >
              {isCreatingParty ? 'Creating Party...' : 'Start Karaoke Party Now!'}
            </button>
            {isAdmin && (
              <>
                <button
                  onClick={handleLaunchFrogLab}
                  disabled={isLaunchingFrogLab}
                  className="flex items-center gap-2 rounded-xl border border-emerald-300/60 bg-gradient-to-r from-emerald-900 to-emerald-600 px-6 py-4 text-lg font-semibold text-white shadow-[0_10px_25px_rgba(16,185,129,0.3)] transition-all hover:scale-105 disabled:opacity-60"
                >
                  <span role="img" aria-hidden="true">🐸</span>
                  {isLaunchingFrogLab ? 'Spinning Up Farkle Party...' : 'Launch Farkle Party'}
                </button>
                <button
                  onClick={handleLaunchImposter}
                  disabled={isLaunchingImposter}
                  className="flex items-center gap-2 rounded-xl border border-purple-300/60 bg-gradient-to-r from-purple-900 to-purple-600 px-6 py-4 text-lg font-semibold text-white shadow-[0_10px_25px_rgba(168,85,247,0.3)] transition-all hover:scale-105 disabled:opacity-60"
                >
                  <span role="img" aria-hidden="true">🎭</span>
                  {isLaunchingImposter ? 'Spinning Up Imposter Party...' : 'Launch Imposter Party'}
                </button>
              </>
            )}
          </div>
          {!reducedModeEnabled && (
            <div className="mt-4">
              <YouTubeStatus 
                className="inline-block"
                onConnect={() => setShowYouTubePrompt(false)}
              />
            </div>
          )}
        </div>

        {/* Custom Party Section */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-8">
          <h2 className="text-2xl font-display font-bold mb-4">🎤 Create Custom Party</h2>
          <p className="text-gray-700 mb-6">
            Customize your karaoke party with a personal name and settings.
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-qr-dark font-semibold mb-2">Party Name:</label>
              <input
                type="text"
                value={partyName}
                onChange={(e) => setPartyName(e.target.value)}
                placeholder="Enter party name (e.g., 'Birthday Bash Karaoke')"
                className="w-full p-3 rounded-lg bg-qr-light text-qr-dark border border-gray-300 focus:border-qr-primary focus:outline-none"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateParty()}
              />
            </div>
            
            <button
              onClick={handleCreateParty}
              disabled={isCreatingParty || !partyName.trim()}
              className="bg-qr-accent hover:bg-purple-500 disabled:bg-gray-400 px-6 py-3 rounded-lg text-white font-semibold transition-colors"
            >
              {isCreatingParty ? 'Creating...' : 'Create Custom Party'}
            </button>
          </div>
        </div>

        {/* Status Cards Row */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <MembershipStatus />
          {reducedModeEnabled ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Core Hosting</h3>
              <p className="text-sm text-gray-600">
                Search, queueing, party launch, and simplified host controls remain available. Account linking and billing stay out of the way here.
              </p>
            </div>
          ) : (
            <YouTubeStatus />
          )}
        </div>

        {/* Features Overview */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <h2 className="text-2xl font-display font-bold mb-6">✨ Your Party Features</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <span className="text-2xl">📱</span>
                <div>
                  <h3 className="font-semibold text-qr-dark">QR Code Access</h3>
                  <p className="text-gray-700 text-sm">Guests scan QR codes to join and request songs</p>
                  <a onClick={() => navigate('/instructions#hosting')} className="text-qr-primary hover:underline text-xs cursor-pointer">How-to →</a>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <span className="text-2xl">🎵</span>
                <div>
                  <h3 className="font-semibold text-qr-dark">Search & Requests</h3>
                  <p className="text-gray-700 text-sm">Guest song search with lightweight YouTube lookup or local catalog fallback</p>
                  <a onClick={() => navigate('/instructions#search')} className="text-qr-primary hover:underline text-xs cursor-pointer">How-to →</a>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <span className="text-2xl">⚡</span>
                <div>
                  <h3 className="font-semibold text-qr-dark">Real-Time Updates</h3>
                  <p className="text-gray-700 text-sm">Live queue updates across all devices</p>
                  <a onClick={() => navigate('/instructions#queue')} className="text-qr-primary hover:underline text-xs cursor-pointer">How-to →</a>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <span className="text-2xl">🎭</span>
                <div>
                  <h3 className="font-semibold text-qr-dark">Playback Display</h3>
                  <p className="text-gray-700 text-sm">Keep the QR code, splash, and active playback on the main screen</p>
                  <a onClick={() => navigate('/instructions#external-display')} className="text-qr-primary hover:underline text-xs cursor-pointer">How-to →</a>
                </div>
              </div>

                <div className="flex items-start space-x-3">
                  <span className="text-2xl">🎛️</span>
                  <div>
                    <h3 className="font-semibold text-qr-dark">Host Controls</h3>
                <p className="text-gray-700 text-sm">Audio output, splash timing, external displays, and queue management</p>
                    <a onClick={() => navigate('/instructions#queue')} className="text-qr-primary hover:underline text-xs cursor-pointer">How-to →</a>
                  </div>
                </div>

              {!reducedModeEnabled && subscription.tier !== 'free' && subscription.isActive && (
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">🎨</span>
                  <div>
                    <h3 className="font-semibold text-qr-dark">Custom Branding</h3>
                    <p className="text-gray-700 text-sm">Your logo, colors, and venue branding on splash screens</p>
                    <a onClick={() => navigate('/instructions#branding')} className="text-qr-primary hover:underline text-xs cursor-pointer">How-to →</a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Host Controls Modal */}
      <HostControlsModal 
        isOpen={showHostControls}
        onClose={() => setShowHostControls(false)}
        partyId={activePartyId || undefined}
        splashCountdown={10}
        onSplashCountdownChange={() => {}}
        splashPaused={false}
        onSplashPausedChange={() => {}}
      />

      {/* Branding Manager Modal */}
      <BrandingManager 
        isOpen={showBrandingManager}
        onClose={() => setShowBrandingManager(false)}
      />

      {/* Venue Ads Manager Modal */}
      <VenueAdsManager
        isOpen={showVenueAdsManager}
        onClose={() => setShowVenueAdsManager(false)}
      />
    </div>
  );
};

export default DashboardPage;